import moment from 'moment';

interface DateRangeProps {
    dateRange: string;
}

class TimeOps {
    static isInDateRange(dateRange) {
        const [startDateString, endDateString] = dateRange.split(' - ');

        const startDate: moment.Moment = moment(startDateString, 'MMMM Do');
        const endDate: moment.Moment = moment(endDateString, 'MMMM Do');

        const startDateMillis = startDate.valueOf();
        const endDateMillis = endDate.valueOf();

        const now = Date.now();

        return now >= startDateMillis && now <= endDateMillis;
    };

    // Convert different date inputs into a Date (officialStartDate)
    // Accepts: Date, ISO string, MM/DD/YYYY, or "MMMM Do" (e.g., "August 8th")
    static getOfficialStartDate(input: Date | string): Date | null {
        if (!input) return null;
        try {
            if (input instanceof Date) {
                return isNaN(input.getTime()) ? null : input;
            }

            // Try common formats using moment for robustness
            const m = moment(input as string, [moment.ISO_8601, 'MM/DD/YYYY', 'M/D/YYYY', 'MMMM Do', 'MMM Do'], true);
            if (m.isValid()) {
                return m.toDate();
            }

            // Fallback to native Date parsing
            const d = new Date(input as string);
            return isNaN(d.getTime()) ? null : d;
        } catch (e) {
            return null;
        }
    }

    // Format a Date like: 8 August 2025 at 20:52:17 UTC-4 (or UTC+5:30 if needed)
    static formatOfficialTimestamp(date: Date): string {
        if (!(date instanceof Date) || isNaN(date.getTime())) return '';

        const day = date.getDate();
        const monthNames = [
            'January','February','March','April','May','June','July','August','September','October','November','December'
        ];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');

        // Timezone offset in minutes; JS returns minutes behind UTC as positive
        const offsetMin = -date.getTimezoneOffset(); // minutes east of UTC
        const sign = offsetMin >= 0 ? '+' : '-';
        const absMin = Math.abs(offsetMin);
        const offHours = Math.floor(absMin / 60);
        const offMins = absMin % 60;
        const tz = offMins === 0 ? `UTC${sign}${offHours}` : `UTC${sign}${offHours}:${String(offMins).padStart(2, '0')}`;

        return `${day} ${month} ${year} at ${hh}:${mm}:${ss} ${tz}`;
    }

    // TO-DO: Update the Date logic so that it includes year and endDate minimum would be the start date.
    static getFormattedDate(inputDate) {
        const formattedDate = inputDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });

        let date = formattedDate.split('at')[0].trim();
        const time = formattedDate.split('at')[1].trim().toLowerCase();
        if (inputDate.getDate() >= 4) {
            date += 'th'
        } else if (inputDate.getDate() == 3) {
            date += 'rd'
        } else if (inputDate.getDate() == 2) {
            date += 'nd'
        } else if (inputDate.getDate() == 1) {
            date += 'st'
        }

        return {
            formattedDate: inputDate.getFullYear() + ' ' + formattedDate,
            comparableDate: [inputDate.getFullYear(), inputDate.getMonth() + 1, inputDate.getDate()],
            date: date,
            time: time,
        }
    }

    /**
     * Format a date to MM/DD/YYYY format
     * Handles Firebase Timestamps, Date objects, and legacy string formats
     */
    static formatToMMDDYYYY(dateInput: any): string {
        if (!dateInput) return 'N/A';
        
        try {
            // Handle Firebase Timestamp objects
            if (dateInput && typeof dateInput === 'object' && dateInput.toDate) {
                const date = dateInput.toDate();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            }
            
            // Handle Date objects
            if (dateInput instanceof Date) {
                const month = String(dateInput.getMonth() + 1).padStart(2, '0');
                const day = String(dateInput.getDate()).padStart(2, '0');
                const year = dateInput.getFullYear();
                return `${month}/${day}/${year}`;
            }
            
            // If not a timestamp or Date, it's legacy/past data
            return 'Legacy Date';
        } catch (error) {
            return 'N/A';
        }
    }

    /**
     * Check if a session has started based on its official start date
     * Uses the same logic as fetchUpcomingSessionsFromFirebase
     */
    static hasSessionStarted(session: any): boolean {
        if (!session || !session.officialStartDate) return false;
        
        try {
            // Use the same logic as fetchUpcomingSessionsFromFirebase
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Start of today
            
            const sessionStartDate = session.officialStartDate.toDate ? 
                session.officialStartDate.toDate() : // Firebase Timestamp
                new Date(session.officialStartDate); // Regular Date object
            
            // Session has started if its official start date is before today
            return sessionStartDate < today;
        } catch (error) {
            console.error('Error checking session start date:', error);
            return false;
        }
    }

    /**
     * Get the start date of a session in a consistent format
     */
    static getSessionStartDate(session: any): Date | null {
        if (!session) return null;
        
        try {
            if (session.officialStartDate) {
                return session.officialStartDate.toDate ? 
                    session.officialStartDate.toDate() : 
                    new Date(session.officialStartDate);
            } else if (session.startDate) {
                return session.startDate.toDate ? 
                    session.startDate.toDate() : 
                    new Date(session.startDate);
            } else if (session.comparableDate) {
                const [year, month, day] = session.comparableDate;
                return new Date(year, month - 1, day);
            }
            return null;
        } catch (error) {
            console.error('Error getting session start date:', error);
            return null;
        }
    }

    /**
     * Get the end date of a session in a consistent format
     */
    static getSessionEndDate(session: any): Date | null {
        if (!session) return null;
        
        try {
            if (session.endDate) {
                return session.endDate.toDate ? 
                    session.endDate.toDate() : 
                    new Date(session.endDate);
            } else {
                // If no explicit end date, fallback to start date (single day session)
                return this.getSessionStartDate(session);
            }
        } catch (error) {
            console.error('Error getting session end date:', error);
            return null;
        }
    }

    /**
     * Check if a session is upcoming (hasn't started yet)
     */
    static isSessionUpcoming(session: any): boolean {
        const startDate = this.getSessionStartDate(session);
        if (!startDate) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Session is upcoming if start date is in the future
        return startDate >= today;
    }

    /**
     * Check if a session is currently ongoing (started but not ended)
     */
    static isSessionOngoing(session: any): boolean {
        const startDate = this.getSessionStartDate(session);
        const endDate = this.getSessionEndDate(session);
        
        if (!startDate || !endDate) return false;
        
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Set start date to beginning of day and end date to end of day
        const sessionStart = new Date(startDate);
        sessionStart.setHours(0, 0, 0, 0);
        
        const sessionEnd = new Date(endDate);
        sessionEnd.setHours(23, 59, 59, 999);
        
        // Session is ongoing if today is between start and end dates (inclusive)
        return today >= sessionStart && today <= sessionEnd;
    }

    /**
     * Check if a session is completely finished (past)
     */
    static isSessionPast(session: any): boolean {
        const endDate = this.getSessionEndDate(session);
        if (!endDate) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const sessionEnd = new Date(endDate);
        sessionEnd.setHours(23, 59, 59, 999);
        
        // Session is past if end date has completely passed
        return sessionEnd < today;
    }

    /**
     * Get the session status as a string
     */
    static getSessionStatus(session: any): 'upcoming' | 'ongoing' | 'past' {
        if (this.isSessionPast(session)) return 'past';
        if (this.isSessionOngoing(session)) return 'ongoing';
        return 'upcoming';
    }

    /**
     * Calculate duration between two dates in days
     * Handles different date formats including Firebase Timestamps
     */
    static calculateDaysDuration(startDate: any, endDate: any): string {
        if (!startDate || !endDate) return '';
        
        try {
            let start: Date, end: Date;
            
            // Convert startDate to Date object
            if (startDate && typeof startDate === 'object' && startDate.toDate) {
                start = startDate.toDate();
            } else if (startDate instanceof Date) {
                start = startDate;
            } else if (typeof startDate === 'string') {
                start = new Date(startDate);
            } else {
                return '';
            }
            
            // Convert endDate to Date object
            if (endDate && typeof endDate === 'object' && endDate.toDate) {
                end = endDate.toDate();
            } else if (endDate instanceof Date) {
                end = endDate;
            } else if (typeof endDate === 'string') {
                end = new Date(endDate);
            } else {
                return '';
            }
            
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                return '';
            }
            
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
            
            return `(${diffDays} day${diffDays === 1 ? '' : 's'})`;
        } catch (error) {
            console.error('Error calculating days duration:', error);
            return '';
        }
    }

    /**
     * Generate an array of date strings between start and end dates
     * Returns dates in YYYY-MM-DD format
     */
    static generateDateRange(startDate: any, endDate: any): string[] {
        try {
            let start: Date;
            let end: Date;

            // Handle Firebase Timestamps and different date formats
            if (startDate && typeof startDate === 'object' && startDate.toDate) {
                start = startDate.toDate();
            } else if (startDate instanceof Date) {
                start = startDate;
            } else if (typeof startDate === 'string') {
                start = new Date(startDate);
            } else {
                return [];
            }

            if (endDate && typeof endDate === 'object' && endDate.toDate) {
                end = endDate.toDate();
            } else if (endDate instanceof Date) {
                end = endDate;
            } else if (typeof endDate === 'string') {
                end = new Date(endDate);
            } else {
                return [];
            }

            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                return [];
            }

            const dates: string[] = [];
            const currentDate = new Date(start);

            while (currentDate <= end) {
                dates.push(currentDate.toISOString().split('T')[0]); // YYYY-MM-DD format
                currentDate.setDate(currentDate.getDate() + 1);
            }

            return dates;
        } catch (error) {
            console.error('Error generating date range:', error);
            return [];
        }
    }
}

export default TimeOps;